import { useQueryClient } from '@tanstack/react-query';
import { Link, Outlet } from '@tanstack/react-router';
import { BarChart3, Blocks, Bot, Cpu, GitPullRequest, LogOut, ScrollText, Settings } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useMe } from '@/lib/hooks';
import { REGIONS, getRegion, setRegion } from '@/lib/regions';
import { saveSession } from '@/lib/session';

const NAV = [
  { to: '/', label: 'Agents', icon: Bot, exact: true },
  { to: '/requests', label: 'Requests', icon: ScrollText },
  { to: '/usage', label: 'Usage', icon: BarChart3 },
  { to: '/delivery', label: 'Delivery', icon: GitPullRequest },
  { to: '/snippets', label: 'Snippets', icon: Blocks },
  { to: '/models', label: 'Models', icon: Cpu },
  { to: '/settings', label: 'Settings', icon: Settings },
] as const;

export const Layout = () => {
  const me = useMe();
  const qc = useQueryClient();
  const region = getRegion();

  return (
    <div className="flex min-h-full">
      <aside className="sticky top-0 hidden h-screen w-52 shrink-0 flex-col border-r border-line bg-panel md:flex">
        <div className="flex items-center gap-2 px-4 py-4">
          <span className="size-6 rounded-md bg-ink p-1.5">
            <span className="block size-full rounded-full bg-cyan-400" />
          </span>
          <span className="font-semibold">Agent Manager</span>
        </div>
        <nav className="flex flex-col gap-0.5 px-2">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              activeOptions={{ exact: 'exact' in n ? n.exact : false }}
              className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] text-ink-2 hover:bg-line-2 hover:text-ink"
              activeProps={{ className: 'bg-line-2 !text-ink font-medium' }}
            >
              <n.icon className="size-4" />
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="mt-auto flex flex-col gap-2 border-t border-line p-3">
          {REGIONS.length > 1 && (
            <div>
              <div className="mb-1 text-[11px] font-medium tracking-wide text-ink-3 uppercase">Data region</div>
              <div className="flex rounded-md border border-line p-0.5">
                {REGIONS.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => {
                      setRegion(r.id);
                      window.location.assign('/');
                    }}
                    className={cn('flex-1 rounded px-2 py-1 text-xs font-medium', r.id === region.id ? 'bg-ink text-white' : 'text-ink-2 hover:text-ink')}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {me.data && (
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-xs font-medium">{me.data.email}</div>
                <div className="text-[11px] text-ink-3">
                  {me.data.role} · {me.data.workspace.name}
                </div>
              </div>
              <button
                title="Sign out"
                className="rounded p-1 text-ink-3 hover:bg-line-2 hover:text-ink"
                onClick={() => {
                  saveSession(null);
                  qc.clear();
                  window.location.assign('/');
                }}
              >
                <LogOut className="size-4" />
              </button>
            </div>
          )}
        </div>
      </aside>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3 border-b border-line bg-panel px-4 py-2 md:hidden">
          <span className="font-semibold">Agent Manager</span>
          <nav className="flex gap-3 text-[13px]">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className="text-ink-2" activeProps={{ className: '!text-ink font-medium' }}>
                {n.label}
              </Link>
            ))}
          </nav>
        </div>
        <main className="mx-auto max-w-[1400px] px-4 py-6 md:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export const PageHeader = ({ title, subtitle, actions }: { title: React.ReactNode; subtitle?: React.ReactNode; actions?: React.ReactNode }) => (
  <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
    <div className="min-w-0">
      <h1 className="text-[20px] font-semibold tracking-tight">{title}</h1>
      {subtitle && <div className="mt-0.5 text-[13px] text-ink-2">{subtitle}</div>}
    </div>
    {actions && <div className="flex items-center gap-2">{actions}</div>}
  </div>
);
